import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { createDayjs } from '@chiroup/core/functions/time';
import { Dayjs } from 'dayjs';

interface AppointmentSettings {
  requireCreditCard: boolean;
  // Set to 'usaepay' or 'dejavoo' when the clinic has card capture wired.
  // Absent means no processor is configured (backend will have flipped
  // requireCreditCard to false in that case).
  processor?: 'usaepay' | 'dejavoo';
  // USAePay-only — public key for the client-side PayJS tokenizer.
  epayPublicKey?: string;
  // Dejavoo-only — ecommToken for the Freedom-to-Design SDK. credentialId is
  // what the backend uses to save the tokenized card to DejavooCustomerTokens.
  dejavoo?: {
    ecommToken: string;
    credentialId: number;
  };
}

export interface AvailabilitySlot {
  provider: string;
  date: string;
  startTime: Dayjs;
  endTime: Dayjs;
  startTimeDisplay: string;
  endTimeDisplay: string;
  duration: number;
  tz: string;
}
export interface AvailabilityResponse {
  [date: string]: {
    [providerId: string]: AvailabilitySlot[];
  };
}

type AvailabilityApiResponse = {
  appointmentSettings: AppointmentSettings;
} & {
  [date: string]: {
    [providerId: string]: {
      provider: string;
      date: string;
      startTime: number;
      endTime: number;
      startTimeDisplay: string;
      endTimeDisplay: string;
      duration: number;
      tz: string;
    }[];
  };
};

const locationService = () => {
  const getLocation = async (path: string): Promise<any> => {
    return ChiroUpAPI.get('api-noauth', `/location/${path}`, {});
  };

  // clinicId-based location lookup for the authed portal — clinics
  // without a vanity URL path are still reachable this way. Mirrors the
  // path-based `getLocation` shape (providers, disciplines, timezone).
  const getLocationByClinicId = async (clinicId: number): Promise<any> => {
    return ChiroUpAPI.get('api-noauth', `/location/clinic/${clinicId}`, {});
  };

  const getAvailability = async ({
    path,
    provider,
    year,
    month,
    treatment,
    timezone,
  }: {
    path: string;
    provider: string | null;
    year: string;
    month: string;
    treatment: number | null;
    timezone: string;
  }): Promise<{
    formattedAvailability: AvailabilityResponse;
    appointmentSettings: AppointmentSettings;
  }> => {
    const response = (await ChiroUpAPI.get('api-noauth', `/location/${path}/availability`, {
      queryParams: {
        path,
        provider,
        year,
        month,
        treatment,
      },
    })) as AvailabilityApiResponse;

    const { appointmentSettings, ...availableDates } = response || {};

    const formattedAvailability = Object.entries(availableDates).reduce(
      (acc: AvailabilityResponse, [dateKey, providerMap]) => {
        acc[dateKey] = Object.entries(providerMap).reduce(
          (providerAcc: { [providerId: string]: AvailabilitySlot[] }, [providerId, slots]) => {
            providerAcc[providerId] = slots?.map(slot => ({
              ...slot,
              startTime: createDayjs({ datetime: slot.startTime, timezone }) as Dayjs,
              endTime: createDayjs({ datetime: slot.endTime, timezone }) as Dayjs,
            }));

            return providerAcc;
          },
          {}
        );

        return acc;
      },
      {}
    );

    return {
      formattedAvailability,
      appointmentSettings,
    };
  };

  // Resolve the Dejavoo manual-entry ecommToken for the selected patient's
  // assigned location (with a primary-location fallback). Authenticated — the
  // browser must tokenize with the same merchant the backend will charge.
  // Returns null when no manual device is configured (can't collect a card).
  const getDejavooManualConfig = async ({
    path,
    patientId,
  }: {
    path: string;
    patientId: string;
  }): Promise<{ processor: 'dejavoo'; ecommToken: string; credentialId: number } | null> => {
    return ChiroUpAPI.get('api', `/location/${path}/dejavoo-manual-config/${patientId}`, {});
  };

  // Default mode of `GET /appointment/{path}` returns all upcoming
  // appointments for the logged-in patient(s). Past appointments are
  // fetched via `getPastAppointments` (server-paginated) so we don't
  // pull a multi-year history on every visit to the page.
  // `patientId` scopes the result to a single patient — the family
  // accounts case otherwise mixes everyone's visits together.
  const getAppointments = async (path: string, patientId?: string | null): Promise<any> => {
    return ChiroUpAPI.get('api', `/appointment/${path}`, {
      queryParams: patientId ? { patientId } : undefined,
    });
  };

  // clinicId-based upcoming appointments for the authed portal (no
  // vanity URL path required). The session is patient-scoped, so this
  // uses the authed `api`.
  const getAppointmentsByClinicId = async (
    clinicId: number,
    patientId?: string | null
  ): Promise<any> => {
    return ChiroUpAPI.get('api', `/appointment/clinic/${clinicId}`, {
      queryParams: patientId ? { patientId } : undefined,
    });
  };

  const getPastAppointments = async (
    path: string,
    { offset, limit, patientId }: { offset: number; limit: number; patientId?: string | null }
  ): Promise<{ items: any[]; hasMore: boolean }> => {
    const queryParams: Record<string, string> = {
      past: 'true',
      offset: String(offset),
      limit: String(limit),
    };

    if (patientId) queryParams.patientId = patientId;

    return ChiroUpAPI.get('api', `/appointment/${path}`, { queryParams });
  };

  // clinicId-based past appointments for the authed portal (no vanity
  // URL path required). Same pagination contract as `getPastAppointments`.
  const getPastAppointmentsByClinicId = async (
    clinicId: number,
    { offset, limit, patientId }: { offset: number; limit: number; patientId?: string | null }
  ): Promise<{ items: any[]; hasMore: boolean }> => {
    const queryParams: Record<string, string> = {
      past: 'true',
      offset: String(offset),
      limit: String(limit),
    };

    if (patientId) queryParams.patientId = patientId;

    return ChiroUpAPI.get('api', `/appointment/clinic/${clinicId}`, { queryParams });
  };

  const cancelAppointment = async (appointmentId: string, reason: string): Promise<void> => {
    return ChiroUpAPI.del('api', `/appointment/${appointmentId}/${reason}`, {});
  };

  // Mints a short-lived signed token for the appointment's .ics URL. The
  // .ics is opened by a webview navigation (iOS "Add to Calendar"), which
  // can't carry the session bearer header — so this authed call hands back
  // a token to put in the URL instead. See `downloadAppointmentIcs`.
  const getCalendarLink = async (appointmentId: string): Promise<{ token: string }> => {
    return ChiroUpAPI.get('api', `/appointment/${appointmentId}/calendar-link`, {});
  };

  return {
    getLocation,
    getLocationByClinicId,
    getAvailability,
    getDejavooManualConfig,
    getAppointments,
    getAppointmentsByClinicId,
    getPastAppointments,
    getPastAppointmentsByClinicId,
    cancelAppointment,
    getCalendarLink,
  };
};

export default locationService();
